import calculateReferenceLine from "./calculateReferenceLine";
import { getEnabledElement } from "component/toolManager/enabledElements.js";
import Cache from "core/cache/index";
import math from "core/math";
import drawLine from "../../../draw/drawLine";
import draw from "../../../draw/draw";
import toolColors from "component/toolManager/toolColors.js";
import makeKey from "core/cache/KeyGen.js";
/**
 * Renders the active reference line.
 *
 * @export @public @method
 * @name renderActiveReferenceLine
 * @param  {Object} context        The canvas context.
 * @param  {Object} eventData      The data associated with the event.
 * @param  {HTMLElement} targetElement    The element on which to render the reference line.
 * @param  {HTMLElement} referenceElement The element referenced by the line.
 * @returns {void}
 */
export default function(context, eventData, targetElement, referenceElement) {
  const targetImage = getEnabledElement(targetElement).image;
  const referenceImage = getEnabledElement(referenceElement).image;

  // Make sure the images are actually loaded for the target and reference
  if (!targetImage || !referenceImage) {
    return;
  }

  const targetSeriesKey = makeKey(
    targetImage.seriesInstanceUID,
    targetImage.modality
  );

  const referenceSeriesKey = makeKey(
    referenceImage.seriesInstanceUID,
    referenceImage.modality
  );

  let metadataCache = new Cache.Metadata();
  const targetImagePlane = metadataCache.getImagePlaneModule(targetSeriesKey);
  targetImagePlane.imagePositionPatient = targetImage.imagePosition;
  targetImagePlane.frameOfReferenceUID = targetImage.frameOfReferenceUID;
  targetImagePlane.rows = targetImage.rows;
  targetImagePlane.columns = targetImage.columns;
  targetImagePlane.rowPixelSpacing = targetImage.pixelSpacing[0];
  targetImagePlane.columnPixelSpacing = targetImage.pixelSpacing[1];

  const referenceImagePlane = metadataCache.getImagePlaneModule(
    referenceSeriesKey
  );
  referenceImagePlane.imagePositionPatient = referenceImage.imagePosition;
  referenceImagePlane.frameOfReferenceUID = referenceImage.frameOfReferenceUID;
  referenceImagePlane.rows = referenceImage.rows;
  referenceImagePlane.columns = referenceImage.columns;
  referenceImagePlane.rowPixelSpacing = referenceImage.pixelSpacing[0];
  referenceImagePlane.columnPixelSpacing = referenceImage.pixelSpacing[1];
  // const referenceImagePlane = cornerstone.metaData.get(
  //   "imagePlaneModule",
  //   referenceImage.imageId
  // );

  // Make sure the target and reference actually have image plane metadata
  if (
    !targetImagePlane ||
    !referenceImagePlane ||
    !targetImagePlane.rowCosines ||
    !targetImagePlane.columnCosines ||
    !targetImagePlane.imagePositionPatient ||
    !referenceImagePlane.rowCosines ||
    !referenceImagePlane.columnCosines ||
    !referenceImagePlane.imagePositionPatient
  ) {
    return;
  }

  // The image planes must be in the same frame of reference
  if (
    targetImagePlane.frameOfReferenceUID !==
    referenceImagePlane.frameOfReferenceUID
  ) {
    return;
  }

  targetImagePlane.rowCosines = math.vector.convertToVector3(
    targetImagePlane.rowCosines
  );
  targetImagePlane.columnCosines = math.vector.convertToVector3(
    targetImagePlane.columnCosines
  );
  targetImagePlane.imagePositionPatient = math.vector.convertToVector3(
    targetImagePlane.imagePositionPatient
  );
  referenceImagePlane.rowCosines = math.vector.convertToVector3(
    referenceImagePlane.rowCosines
  );
  referenceImagePlane.columnCosines = math.vector.convertToVector3(
    referenceImagePlane.columnCosines
  );
  referenceImagePlane.imagePositionPatient = math.vector.convertToVector3(
    referenceImagePlane.imagePositionPatient
  );

  // The image plane normals must be > 30 degrees apart
  const targetNormal = targetImagePlane.rowCosines
    .clone()
    .cross(targetImagePlane.columnCosines);
  const referenceNormal = referenceImagePlane.rowCosines
    .clone()
    .cross(referenceImagePlane.columnCosines);
  let angleInRadians = targetNormal.angleTo(referenceNormal);

  angleInRadians = Math.abs(angleInRadians);
  if (angleInRadians < 0.5) {
    // 0.5 radians = ~30 degrees
    return;
  }

  const referenceLine = calculateReferenceLine(
    targetImagePlane,
    referenceImagePlane
  );

  if (!referenceLine) {
    return;
  }

  //const color = toolColors.getActiveColor();
  const color = "#00ff00";

  // Draw the referenceLines
  context.setTransform(1, 0, 0, 1, 0, 0);

  draw(context, context => {
    let eventDataElementStub = getEnabledElement(eventData.element);
    eventDataElementStub.needsRedraw = true;
    //console.log(new Error().stack);
    drawLine(
      context,
      eventDataElementStub,
      referenceLine.start,
      referenceLine.end,
      { color }
    );
  });
}
